import {
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography
} from '@material-ui/core'
import Compressor from 'compressorjs'
import { SendHorizontal, Upload, X, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { useStyles } from './style'
import { getFileIconSrc } from '../../utils/fileIconMapper'

export const UploadDropZone = ({
  ticketId,
  ticket,
  children,
  fileInputRef,
  parentDrawerOpen = false
}) => {
  const classes = useStyles()
  const [isDragging, setIsDragging] = useState(false)
  const [uploadFiles, setUploadFiles] = useState([])
  const [, setUploadProgress] = useState({})
  const [isUploading, setIsUploading] = useState(false)

  const internalFileInputRef = useRef(null)
  const dropAreaRef = useRef(null)
  const previewContainerRef = useRef(null)

  const inputRef = fileInputRef || internalFileInputRef
  const isProcessingPaste = useRef(false)

  useEffect(() => {
    const handleWheel = e => {
      if (previewContainerRef.current) {
        e.preventDefault()
        previewContainerRef.current.scrollLeft += e.deltaY
      }
    }

    const container = previewContainerRef.current
    if (container) {
      container.removeEventListener('wheel', handleWheel)
      container.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel)
      }
    }
  }, [uploadFiles])

  const containsFiles = e => {
    if (!e.dataTransfer || !e.dataTransfer.types) return false
    return Array.from(e.dataTransfer.types).includes('Files')
  }

  const handleDragEnter = e => {
    e.preventDefault()
    e.stopPropagation()
    if (!containsFiles(e)) return
    setIsDragging(true)
  }

  const handleDragOver = e => {
    e.preventDefault()
    e.stopPropagation()
    if (!containsFiles(e)) return
    if (!isDragging) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = e => {
    e.preventDefault()
    e.stopPropagation()

    if (!dropAreaRef.current.contains(e.relatedTarget)) {
      setIsDragging(false)
    }
  }

  const handleDrop = e => {
    e.preventDefault()
    e.stopPropagation()

    setIsDragging(false)

    if (!containsFiles(e)) return

    if (ticket.status === 'pending' && !ticket.isGroup) {
      toastError(
        new Error('Atendimento está pendente. Não é possível enviar arquivos.')
      )
      return
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files)
      const newFiles = droppedFiles.map(file => ({
        file,
        id: Math.random().toString(36).substring(2, 9),
        name: file.name,
        progress: 0,
        status: 'pending',
        preview: null
      }))

      newFiles.forEach(fileItem => {
        if (fileItem.file.type.startsWith('image/')) {
          const reader = new FileReader()
          reader.onload = e => {
            setUploadFiles(prevFiles =>
              prevFiles.map(f =>
                f.id === fileItem.id ? { ...f, preview: e.target.result } : f
              )
            )
          }
          reader.readAsDataURL(fileItem.file)
        }
      })

      setUploadFiles(prev => [...prev, ...newFiles])
    }
  }

  const removeFile = fileId => {
    setUploadFiles(prev => prev.filter(file => file.id !== fileId))
  }

  const removeAllFiles = () => {
    setUploadFiles([])
    setUploadProgress({})
  }

  const uploadFilesToServer = async () => {
    if (ticket.status === 'closed') {
      toastError(
        new Error('Atendimento está fechado. Não é possível enviar arquivos.')
      )
      return
    }

    setIsUploading(true)

    const filesToUpload = [...uploadFiles]

    for (const fileItem of filesToUpload) {
      if (fileItem.status !== 'pending') continue

      fileItem.status = 'uploading'
      setUploadFiles([...filesToUpload])

      const formData = new FormData()
      formData.append('fromMe', true)

      try {
        if (fileItem.file.type.startsWith('image/')) {
          await new Promise(resolve => {
            new Compressor(fileItem.file, {
              quality: 0.7,
              success(compressedFile) {
                formData.append('medias', compressedFile)
                formData.append('body', fileItem.file.name)
                resolve()
              },
              error(err) {
                console.error('Error compressing image:', err)
                // If compression fails, use original file
                formData.append('medias', fileItem.file)
                formData.append('body', fileItem.file.name)
                resolve()
              }
            })
          })
        } else {
          formData.append('medias', fileItem.file)
          formData.append('body', fileItem.file.name)
        }

        await api.post(`/messages/${ticketId}`, formData, {
          onUploadProgress: event => {
            const progress = Math.round((event.loaded * 100) / event.total)
            setUploadProgress(prev => ({
              ...prev,
              [fileItem.id]: progress
            }))

            fileItem.progress = progress
            setUploadFiles([...filesToUpload])
          }
        })

        fileItem.status = 'completed'
        setUploadFiles([...filesToUpload])
      } catch (err) {
        fileItem.status = 'error'
        setUploadFiles([...filesToUpload])
        toastError(err)
      }
    }

    // Once all files are processed, clear completed files
    setUploadFiles([])
    setUploadProgress({})
    // setIsUploading(false)
    setTimeout(() => {
      setIsUploading(false) // Mover para depois de um pequeno delay
    }, 50)
  }

  const getFileIcon = file => {
    if (file.preview) {
      return (
        <div className={classes.previewImage}>
          <img
            src={file.preview}
            alt={file.name}
            onClick={() => removeFile(file.id)}
          />
          {file.status === 'uploading' && (
            <div className={classes.imageLoadingOverlay}>
              <CircularProgress
                size={30}
                variant="determinate"
                value={file.progress}
                className={classes.imageProgress}
              />
              <span className={classes.progressPercentage}>
                {file.progress}%
              </span>
            </div>
          )}
        </div>
      )
    }

    // Utiliza a função importada
    const iconSrc = getFileIconSrc(file.name, file.file.type)
    const extension = file.name.split('.').pop().toLowerCase()

    return (
      <div className={classes.fileIconContainer}>
        <div className={classes.fileIcon}>
          <img
            src={iconSrc}
            alt={`Arquivo .${extension}`}
            className={classes.fileIconImage}
          />
          {file.status === 'uploading' && (
            <div className={classes.fileLoadingOverlay}>
              <CircularProgress
                size={30}
                variant="determinate"
                value={file.progress}
                className={classes.fileProgress}
              />
              <span className={classes.progressPercentage}>
                {file.progress}%
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderFilePreview = () => {
    // if (uploadFiles.length === 0) return null

    return (
      <div
        className={`${classes.previewContainer} ${parentDrawerOpen ? classes.previewContainerWithDrawer : ''}`}
        aria-label="Arquivos selecionados para envio"
      >
        <div className={classes.previewContent} ref={previewContainerRef}>
          {uploadFiles.map(fileItem => (
            <div key={fileItem.id} className={classes.previewItem}>
              {getFileIcon(fileItem)}
              <div className={classes.previewInfo}>
                <span className={classes.previewName}>{fileItem.name}</span>
              </div>
              <div className={classes.previewActions}>
                <div className={classes.progressContainer}>
                  {fileItem.status === 'uploading' && (
                    <LinearProgress
                      variant="determinate"
                      value={fileItem.progress}
                    />
                  )}
                  {fileItem.status === 'error' && (
                    <Typography variant="caption" color="error">
                      Erro ao enviar arquivo
                    </Typography>
                  )}
                </div>
                <IconButton
                  className={classes.cancelButton}
                  size="small"
                  onClick={() => removeFile(fileItem.id)}
                >
                  <X />
                </IconButton>
              </div>
            </div>
          ))}
        </div>

        <div className={classes.uploadButtonContainer}>
          <button
            onClick={removeAllFiles}
            className={classes.cancelButtonAllFiles}
            aria-label="Remover todos os arquivos selecionados"
          >
            <XCircle />
          </button>
          <span className={classes.filesCount}>{uploadFiles.length}</span>

          {uploadFiles.some(file => file.status === 'pending') && (
            <button
              onClick={uploadFilesToServer}
              disabled={isUploading}
              className={classes.uploadButton}
              style={{
                cursor: isUploading ? 'not-allowed' : 'pointer'
              }}
              aria-label="Enviar arquivos salvos"
            >
              <SendHorizontal />
            </button>
          )}
        </div>
      </div>
    )
  }

  const handleFileSelect = e => {
    if (ticket.status === 'pending' && !ticket.isGroup) {
      toastError(
        new Error('Atendimento está pendente. Não é possível enviar arquivos.')
      )
      return
    }

    if (!e.target.files || e.target.files.length === 0) return

    const selectedFiles = Array.from(e.target.files)
    processFiles(selectedFiles)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handlePaste = e => {
    e.stopPropagation()

    if (isProcessingPaste.current) return

    if (ticket.status === 'pending' && !ticket.isGroup) return

    e.preventDefault()
    const clipboardData = e.clipboardData
    let hasFilesOrImages = false

    if (clipboardData.files && clipboardData.files.length > 0) {
      processFiles(Array.from(clipboardData.files))
      hasFilesOrImages = true
    } else if (clipboardData.items && clipboardData.items.length > 0) {
      const items = Array.from(clipboardData.items)
      const imageItem = items.find(item => item.type.indexOf('image') !== -1)
      if (imageItem) {
        const blob = imageItem.getAsFile()
        if (blob) {
          processFiles([blob])
          hasFilesOrImages = true
        }
      }
    }

    if (hasFilesOrImages) {
      isProcessingPaste.current = true
      setTimeout(() => {
        isProcessingPaste.current = false
      }, 100)
      return
    }
  }

  const processFiles = files => {
    const newFiles = files.map(file => ({
      file,
      id: Math.random().toString(36).substring(2, 9),
      name: file.name || `pasted-image-${new Date().getTime()}.png`,
      progress: 0,
      status: 'pending',
      preview: null
    }))

    newFiles.forEach(fileItem => {
      if (fileItem.file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = e => {
          setUploadFiles(prevFiles =>
            prevFiles.map(f =>
              f.id === fileItem.id ? { ...f, preview: e.target.result } : f
            )
          )
        }
        reader.readAsDataURL(fileItem.file)
      }
    })

    setUploadFiles(prev => {
      const updatedFiles = [...prev, ...newFiles]
      return updatedFiles
    })
  }

  const renderFileInput = () => {
    const isDisabled = ticket.status === 'pending' && !ticket.isGroup

    return (
      <>
        <input
          multiple
          type="file"
          id="upload-button"
          ref={inputRef}
          disabled={isDisabled}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </>
    )
  }

  return (
    <div
      className={classes.dropZoneWrapper}
      ref={dropAreaRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onPaste={handlePaste}
      role="region"
      aria-label="Área de upload de arquivos para o atendimento"
    >
      {renderFileInput()}
      {children}

      {isDragging && (
        <div className={classes.dropZoneOverlay}>
          <div className={classes.dropZoneContent}>
            <div className={classes.dropZoneIcon}>
              <Upload size={48} />
            </div>
            <Typography variant="h6" className={classes.dropZoneText}>
              Solte os arquivos aqui para fazer upload
            </Typography>
          </div>
        </div>
      )}

      {uploadFiles.length > 0 && renderFilePreview()}
    </div>
  )
}
